import React, { useEffect, useState } from 'react';
import AssessmentItem from 'components/assessment-item';
import ModuleItem from 'components/module-item';
import { useRouter } from 'next/dist/client/router';
import styled from 'styled-components';
import i18n, { SettingsType } from 'utils/i18n';
import Layout from './master-layout';
import {
  learningPathStatus,
  isLearningPathComplete,
  isModuleCompleted,
  isUnitCompleted,
  useUserData,
} from 'utils/user';
import BookmarkButton from 'components/bookmark-button';
import Button from 'components/button';
import MarkdownRender from 'components/markdown-render';
import MetaInfo from 'components/meta-info';
import MetaTags from 'components/meta-tags';
import { mqFrom } from 'styles/styles';
import Image from 'next/legacy/image';

import ProgressHexagon from 'components-new/progress-hexagon';

type LearningPathPageProps = {
  data: LearningPath;
};

export const LearningPathPage: React.FC<LearningPathPageProps> = ({ data }) => {
  const { locale, push } = useRouter();
  const { dictionary } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType;
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const { achievements } = useUserData();

  useEffect(() => {
    if (data.hidden && data.modules && data.modules.length === 1) {
      // Redirect to the learning path assessment page
      console.log('data.hidden', data.hidden, { data });
      push(data.modules[0].meta.path);
    }
  }, [data.hidden]);

  useEffect(() => {
    // Find first incomplete module
    const initExpanded = {};
    const nextModule = data.modules.find((mod) => !isModuleCompleted(achievements, mod));
    if (nextModule) {
      initExpanded[nextModule.meta.id] = true;
    }

    // Find module with incomplete unit
    if (data.modules?.length) {
      data.modules
        .filter((item) => item.meta.type === 'Module')
        .filter((item) => !isModuleCompleted(achievements, item))
        .forEach((mod: Module) => {
          if (mod.units.find((unit) => isUnitCompleted(achievements, unit))) {
            initExpanded[mod.meta.id] = true;
          }
        });
    }

    setExpanded(initExpanded);

    // Find modules with completed units
  }, [data.meta.id, JSON.stringify(achievements)]); // eslint-disable-line

  let firstUnit: Unit | null = null;
  let hasCompletedUnits = false;

  if (data.hidden && data.modules && data.modules.length === 1) {
    return <></>;
  }

  if (data.modules?.length) {
    data.modules.forEach((mod) => {
      if (mod.meta.type === 'Module') {
        (mod as Module).units.forEach((unit) => {
          if (isUnitCompleted(achievements, unit)) {
            hasCompletedUnits = true;
          }
          if (!isUnitCompleted(achievements, unit) && !firstUnit) {
            firstUnit = unit;
          }
        });
      }
    });
  }
  // Hvis LearningPath er afsluttet, så er alle modules collapsed og knapper er væk
  return (
    <Layout breadcrumb={data.meta.breadcrumb} title={data.meta.title} description={data.meta.description}>
      <StyledLearningPathArticle className="learning-path">
        <div className="description small-outer-padding">
          <div className="inner">
            <>
              <ProgressHexagon status={learningPathStatus(achievements, data)} />
              <div className="image">
                {data.icon ? (
                  <div className="header-icon">
                    <Image
                      src={data.icon.url}
                      width={data.icon.width}
                      height={data.icon.height}
                      alt={data.icon.title}
                      sizes="112px"
                    />
                  </div>
                ) : (
                  <div className="empty-header-icon"></div>
                )}
              </div>
              <div className="content">
                <h1>{data.title}</h1>
                <MetaInfo readingTime={data.meta.readingTime} type="LEARNINGPATH" unitsCount={data.modules?.length} />
                <MetaTags tags={data.meta.tags} />

                <MarkdownRender markdown={data.body} />
                {data.prerequisites && (
                  <>
                    <h2 className="h3">{dictionary.prerequisites}</h2>
                    <MarkdownRender markdown={data.prerequisites} />
                  </>
                )}
                <div className="buttons">
                  {/* Start button if first module doesn't have any completed units - Link to first unit  */}
                  {!hasCompletedUnits && firstUnit && (
                    <Button
                      mode="primary"
                      alignIcon="right"
                      iconName="chevron-right"
                      linkProps={{ href: firstUnit.meta.path }}
                    >
                      {dictionary.start}
                    </Button>
                  )}
                  {/* Continue button if next incomplete module has incompleted units - Link to first unit */}
                  {hasCompletedUnits && firstUnit && (
                    <Button
                      mode="primary"
                      alignIcon="right"
                      iconName="chevron-right"
                      linkProps={{ href: firstUnit.meta.path }}
                    >
                      {dictionary.continue}
                    </Button>
                  )}
                  <BookmarkButton learningPathId={data.meta.id} />
                </div>
              </div>
            </>
          </div>
        </div>

        {/* Modules */}
        <div className="small-outer-padding modules-list">
          <h3>{dictionary.modulesInLearningPath}</h3>
          <div className="modules">
            {data.modules?.map((item) => {
              if (item.meta.type === 'Assessment') {
                return (
                  <AssessmentItem
                    key={item.meta.id}
                    assessment={item as Assessment}
                    completed={isLearningPathComplete(achievements, data)}
                  />
                );
              }

              return (
                <ModuleItem
                  key={item.meta.id}
                  module={item as Module}
                  expanded={expanded[item.meta.id] || false}
                  onToggle={() => {
                    const newExpandedObj = { ...expanded, [item.meta.id]: !expanded[item.meta.id] };
                    setExpanded(newExpandedObj);
                    // localStorage.setItem(expandedKey, JSON.stringify(newExpandedObj));
                  }}
                />
              );
            })}
          </div>
        </div>
      </StyledLearningPathArticle>
    </Layout>
  );
};

const StyledLearningPathArticle = styled.article`
  margin-bottom: 3rem;

  .description {
    background: var(--body-background);
    padding-top: 3rem;
    padding-bottom: 3rem;

    .inner {
      position: relative;
      display: flex;
      flex-direction: column;

      ${mqFrom.tablet} {
        flex-direction: row;
      }

      .xp-hexagon {
        right: 3rem;
      }

      .image {
        width: 112px;

        .empty-header-icon {
          width: 112px;
          height: 112px;
          background: #52b3c0;
          border: 3px solid #438c94;
          border-radius: 50%;
        }
      }

      .content {
        padding: 1.5rem;
        flex: 1 1 0;
      }

      .h3 {
        font-size: 1.125rem;
        margin-bottom: 0;
      }

      .buttons {
        display: flex;
        gap: 0.5rem;
        margin: 1rem 0;
      }
    }
  }

  .modules {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .modules-list {
    > h3 {
      margin: 1.5rem 0;
    }
  }
`;

export default LearningPathPage;
